import React, { lazy, Suspense } from "react";
import type { MarkdownProps } from "./Markdown.types";
import "./Markdown.scss";

/**
 * 后台富文本（运营用 MDEditor 编辑）的统一渲染组件。
 *
 * 为什么从 @uiw/react-markdown-preview 换成 marked：
 * 那个库是给文档站做的，带 react-markdown + 13 个 remark/rehype 插件 + prism 代码高亮
 * + copy-to-clipboard，打出来 996KB（gz 340KB）。实测 Chrome Performance（4x CPU 节流）：
 * 它的 v8.evaluateModule 单独占住主线程 419ms，是首屏之后最大的一次卡顿源 —— 而我们
 * 只是拿它渲染运营配的几行说明文字（页脚、活动规则、弹窗文案）。
 * marked 是 ~14KB(gz) 的纯解析器，GFM 表格、原始 HTML 都支持。
 *
 * 与旧实现的行为对齐（已逐项验过，含线上真实配置数据）：
 *   · gfm: true —— 旧库挂了 remark-gfm，表格 / 删除线 / 自动链接都要保留；
 *   · breaks: false —— 旧库【没有】挂 remark-breaks，单个换行不产生 <br>，保持一致；
 *   · 原始 HTML 直出 —— 旧库挂了 rehype-raw，运营写的 <div style="..."> 必须原样生效
 *     （线上样本里 29 个 style 属性一个不少）。项目对后台内容一贯信任（协议、活动规则等
 *     7 处直接 dangerouslySetInnerHTML），故不额外引消毒库，与既有口径一致；
 *   · 容器仍带 wmde-markdown 类 —— Markdown.scss 那 265 行样式全挂在它上面。
 *
 * 解析层放在 MarkdownBody 里 lazy：本组件被 10 处引用，同步 import 会让 marked 被提进
 * 公共 chunk 落到首屏，而这些内容没有一处属于首屏关键渲染。
 */
const MarkdownBody = lazy(() => import("./MarkdownBody"));

export const Markdown: React.FC<MarkdownProps> = ({
  source,
  className = "",
  style,
}) => {
  if (!source) {
    return null;
  }

  return (
    <div className={`markdown-content ${className}`} style={style}>
      {/* fallback 用 null 而不是 Loading：chunk 只有 ~14KB(gz)，转瞬即到，
          放骨架反而是一闪而过的噪点。 */}
      <Suspense fallback={null}>
        <MarkdownBody source={source} />
      </Suspense>
    </div>
  );
};

export default Markdown;
